import entities from './data/entities.json';
import type { StatusKind } from './sim/status.js';

export type ClassId = 'warrior' | 'wizard' | 'ranger' | 'cleric' | 'druid' | 'warlock';
export const CLASS_IDS = Object.keys(entities.classes) as ClassId[];

export type AttackStyle = 'melee' | 'ranged';

/** The three Warcraft-3-style core attributes. */
export type AttributeId = 'strength' | 'agility' | 'energy';
export const ATTRIBUTE_IDS: AttributeId[] = ['strength', 'agility', 'energy'];

/** Ordered ability slots. `r` is the ultimate. */
export type AbilitySlot = 'q' | 'w' | 'e' | 'r';
export const ABILITY_SLOTS: AbilitySlot[] = ['q', 'w', 'e', 'r'];

export interface ClassStats {
  name: string;
  attackStyle: AttackStyle;
  /** BASE hp; effective = hp + strength * hpPerStrength. */
  hp: number;
  attackRange: number;
  /** BASE attack cooldown (ms); effective = attackSpeed / (1 + agility * attackSpeedPctPerAgility). */
  attackSpeed: number;
  /** BASE damage; effective = damage + primaryAttrValue * damagePerPrimary. */
  damage: number;
  radius: number;
  /** BASE armor; effective = armor + agility * armorPerAgility. */
  armor: number;
  /** BASE max mana; effective = mana + energy * manaPerEnergy. */
  mana: number;
  /** BASE mana regen per second; effective = manaRegen + energy * manaRegenPerSecPerEnergy. */
  manaRegen: number;
  strength: number;
  agility: number;
  energy: number;
  primaryAttribute: AttributeId;
  tint: string;
}

/** Tunable attribute -> effective-stat conversion constants. */
export interface AttributeFormula {
  hpPerStrength: number;
  hpRegenPerSecPerStrength: number;
  armorPerAgility: number;
  attackSpeedPctPerAgility: number;
  manaPerEnergy: number;
  manaRegenPerSecPerEnergy: number;
  damagePerPrimary: number;
}

/**
 * feature 2.3 — one shop catalog entry: a permanent, run-long raw-stat upgrade
 * bought with the player's own gold during the wave break. The catalog
 * (`EntitiesData.shop`) is an ordered, fixed list (no rotating stock).
 */
export interface ShopItem {
  /** Stable catalog id (also the key in a player's purchase-count map). */
  id: string;
  /** Which stat a purchase raises. */
  stat: 'strength' | 'agility' | 'energy' | 'maxHp' | 'moveSpeed';
  /** HUD label. */
  label: string;
  /** Amount added to `stat` per purchase. */
  amountPerBuy: number;
  /** Cost of the first purchase. */
  baseCost: number;
  /** Added to the cost for every purchase already made (`shopCost`). */
  costStep: number;
  /** Hard cap on purchases of this item, per player, per run. */
  maxBuys: number;
}

/**
 * feature 4.1 — the XP → level curve + level-up reward tunables
 * (`EntitiesData.progression`). See `sim/progression.ts` for the curve helpers.
 * `classGrowth` maps every class to the `{ strength, agility, energy }` it gains
 * automatically per level ABOVE 1 (the primary attribute grows fastest); this is
 * folded into effective stats by `World.recomputeStats`.
 */
export interface Progression {
  levelCap: number;
  xpBase: number;
  xpGrowth: number;
  skillPointsPerLevel: number;
  freeAttrPointsPerLevel: number;
  /** feature 4.2 — max points investable in one ability (1 to activate + room
   * for the 4.3 talent nodes). `spendAbilityPoint` rejects at this cap. */
  abilityPointCap: number;
  /** feature 4.2 — minimum level before the FIRST point can go into the `r`
   * (ultimate) slot. Only gates that first point; irrelevant once `r` is active. */
  ultLevelGate: number;
  classGrowth: Record<ClassId, { strength: number; agility: number; energy: number }>;
}

/** feature 3.1 — item rarity. Cosmetic in 3.1 (drives HUD colour only). */
export type ItemRarity = 'common' | 'rare' | 'epic';

/**
 * feature 3.1 — the stat bonuses an item grants while held. A partial map; every
 * field is summed across the player's inventory into a `StatBonuses` by
 * `itemBonuses` and applied through `effectiveStats` / `World.recomputeStats`.
 * `attackSpeedPct` is a fractional attack-speed increase folded into the same
 * divisor as agility. A pure "+1 to a skill" item (feature 4.5) carries an empty
 * `stats` and only a `skillBonus`.
 */
export interface ItemStats {
  maxHp?: number;
  armor?: number;
  damage?: number;
  moveSpeed?: number;
  strength?: number;
  agility?: number;
  energy?: number;
  attackSpeedPct?: number;
}

/**
 * feature 3.1 — one entry of the fixed item catalog (`DATA.items`, keyed by id).
 * Every catalog item is purchasable from the shop during the wave break for
 * `cost` gold; held items sum their `stats` into the player's effective stats.
 *
 * feature 4.5 — a rare / epic item may also carry `skillBonus`: while held it
 * grants `amount` effective points to ability `slot` (`effectiveAbilityPoints` /
 * `abilityActivated` in `sim/talents.ts`), which both activates an un-activated
 * ability and counts toward its talent "parent activated" gate. It does NOT
 * spend or refund skill points and it is on top of the talent point cap. The
 * "+1 skill" items carry an empty `stats`.
 */
export interface ItemDef {
  id: string;
  name: string;
  rarity: ItemRarity;
  cost: number;
  stats: ItemStats;
  /** feature 4.5 — `+amount` effective points to ability `slot` while held. */
  skillBonus?: { slot: AbilitySlot; amount: number };
}

/**
 * feature 3.3 — one forge recipe (`DATA.recipes`, an ordered list). `inputs` is
 * 2–3 catalog item ids (duplicates allowed, e.g. `["whetstone", "whetstone"]`);
 * `output` is a catalog item id whose `stats` are, per field, `>=` the summed
 * inputs' same field and strictly greater on at least one (asserted by
 * `forge.test.ts`). Every catalog item — output items included — stays
 * shop-buyable (feature 3.1); forging is just a free / faster path to one.
 */
export interface RecipeDef {
  id: string;
  inputs: string[];
  output: string;
}

/**
 * feature 3.4 — the trash-mob enemy item drop table (`DATA.drops`). `chance` is
 * the probability (0–1) a non-boss enemy death drops an item; `weights` maps an
 * `items` catalog id to its relative pick weight (see `pickWeighted`);
 * `pickupRadius` is the walk-over auto-pickup distance in world px. Bosses get
 * their own drop rules later (increment 5).
 */
export interface DropTable {
  chance: number;
  weights: Record<string, number>;
  pickupRadius: number;
}

/**
 * feature 4.3 — one talent-node effect. `target` names a numeric field of the
 * parent `AbilityDef` (`damage` / `tickDamage` / `heal` / `cooldownMs` /
 * `manaCost` / `radius` / `range` / `castRange` / `maxRange` / `durationMs` /
 * `armorBonus` / `shieldAmount` / `regenPerTick` / `attackSpeedBonus`) or the
 * synthetic `damagePct` (a final-damage multiplier — `effectiveAbility` folds it
 * into `damage` + `scale`). `mode` `addFlat` → `field += value`; `addPct` →
 * `field *= (1 + value)`, applied once per owned node (additive-pct is
 * equivalent for the current 1-node-per-target placeholder set).
 */
export interface TalentEffect {
  target: string;
  mode: 'addFlat' | 'addPct';
  value: number;
}

/**
 * feature 4.3 — one talent-tree node. Costs 1 skill point; inert until the
 * parent ability is activated. See `sim/talents.ts` for the node → ability
 * lookup and `effectiveAbility`.
 */
export interface TalentNode {
  id: string;
  name: string;
  effect: TalentEffect;
}

/**
 * feature 1.5 — the ability targeting taxonomy (criterion A). Every ability's
 * `targeting` is exactly one of:
 * - `point` — the cursor position is the impact / target point (nuke,
 *   `atPoint` AoEs incl. windup ground-AoEs, charge landing).
 * - `direction` — the cursor gives a direction FROM the caster, not a point
 *   (projectile skillshot, meleeArc / cone, blink-away).
 * - `self` — no cursor input (selfGuard, healAoe, partyBuff, whirlwind,
 *   self-centred aoe).
 *
 * The cast handlers already do the right cursor math; `targeting` is data that
 * describes the input contract (used for docs / tooling / future cast UI).
 */
export type AbilityTargeting = 'point' | 'direction' | 'self';
export type AbilityKind =
  // generic reusable kinds (1.1 origin, still used by every non-Warrior class)
  | 'nuke'
  | 'blink'
  | 'aoe'
  // 1.2 Warrior kit kinds
  | 'charge'
  | 'meleeArc'
  | 'selfGuard'
  | 'whirlwind'
  // 1.5 reusable kinds — a travelling skillshot (Ranger Piercing Shot). Replaces
  // the 1.3a `lineShot` hitscan, which had no remaining user after the switch.
  | 'projectile'
  // 1.3b reusable kinds (Cleric / Druid)
  | 'healAoe'
  | 'partyBuff';

export interface AbilityDef {
  id: string;
  name: string;
  targeting: AbilityTargeting;
  kind: AbilityKind;
  manaCost: number;
  cooldownMs: number;
  /** kind: nuke — max distance from caster to the affected enemy. */
  castRange?: number;
  /**
   * feature 1.3a — primary-attribute damage scaling: effective damage =
   * damage + primaryAttrValue * scale (see World.abilityDamage). Used by
   * nuke / aoe / meleeArc / projectile / charge / whirlwind (the Warrior kit,
   * whose primary attribute is strength).
   */
  scale?: number;
  /**
   * feature 1.5 — kind: aoe (`atPoint`) — a windup delay in ms. On cast, mana +
   * cooldown are charged immediately and a danger-zone telegraph is shown at the
   * locked point; the AoE effect body resolves `windupMs` later (rounded up to a
   * whole tick). Meteor / Entangling Storm / Entangling Roots.
   */
  windupMs?: number;
  /** feature 1.5 — kind: projectile — travel speed in px/s (Piercing Shot). */
  projectileSpeed?: number;
  /**
   * feature 1.5 — kind: projectile — the projectile hits an enemy when the gap
   * between their centres is `<= hitRadius + enemyRadius` (Piercing Shot).
   */
  hitRadius?: number;
  /**
   * feature 1.3c — kind: nuke — the caster is healed (via `dealHeal`, so capped
   * at maxHp, no res) for `damageDealt * lifestealFrac`, summed over every enemy
   * the ability damages (Warlock Drain Life).
   */
  lifestealFrac?: number;
  /** kind: blink / charge — the teleport / dash distance is clamped to this. */
  maxRange?: number;
  /**
   * kind: blink — descriptive metadata only. Since feature 1.4 `castBlink` is
   * unconditionally away-only (the caster hops a fixed `maxRange` directly AWAY
   * from the target point — Ranger Disengage, the only blink), so this flag is
   * no longer read by the sim; it is retained to document the ability's intent
   * and is still set (`"away": true`) on Disengage.
   */
  away?: boolean;
  /**
   * feature 1.3b — kind: healAoe — base heal per ally; effective heal =
   * heal + primaryAttrValue * scale (Energy-scaled, `abilityDamage`-style).
   */
  heal?: number;
  /**
   * feature 1.3b — kind: aoe — when true the radius is centred on the target
   * point instead of the caster (Druid Entangling Roots). Default: caster.
   */
  atPoint?: boolean;
  /**
   * feature 1.3b — apply a `regen` status that heals this much every
   * `tickIntervalMs` for `durationMs`. kind: selfGuard (Druid Regrowth — caster
   * only); kind: healAoe (Cleric Sanctuary — every healed ally).
   */
  regenPerTick?: number;
  /**
   * feature 1.4 — kind: selfGuard — apply an `attackSpeedBonus` status of this
   * magnitude to the caster for `durationMs` (Ranger Rapid Fire).
   */
  attackSpeedBonus?: number;
  /**
   * feature 1.4 — kind: selfGuard — apply a `damageBonus` status of this
   * magnitude to the caster for `durationMs` (Ranger Rapid Fire).
   */
  damageBonus?: number;
  /** kind: aoe / whirlwind — effect radius around the caster. */
  radius?: number;
  /** kind: aoe / charge / meleeArc — flat/base damage. */
  damage?: number;
  /** kind: charge — width of the dash corridor used for the first-enemy check. */
  corridorWidth?: number;
  /** kind: charge — how far the first enemy hit is knocked back, in px. */
  knockbackDistance?: number;
  /** kind: charge — stun duration applied to the first enemy hit, in ms. */
  stunMs?: number;
  /** kind: meleeArc — radius of the cleave / volley cone around the caster;
   * kind: projectile — max travel distance from the caster before it expires. */
  range?: number;
  /** kind: meleeArc — half-angle of the damage cone, in degrees. */
  arcHalfAngleDeg?: number;
  /** kind: selfGuard / whirlwind / healAoe (Sanctuary buff) — effect duration, in ms. */
  durationMs?: number;
  /** kind: selfGuard — absorb pool granted to the caster as a `shield` status. */
  shieldAmount?: number;
  /** kind: selfGuard — flat armor granted while active. */
  armorBonus?: number;
  /** kind: selfGuard — incoming-damage multiplier is (1 - damageReduction). */
  damageReduction?: number;
  /** kind: selfGuard — enemies within this radius are taunted onto the caster. */
  tauntRadius?: number;
  /** kind: selfGuard — taunt duration applied to nearby enemies, in ms. */
  tauntDurationMs?: number;
  /**
   * ms between periodic ticks. kind: whirlwind (damage ticks, first on cast);
   * selfGuard / healAoe — interval for the applied `regen` status.
   */
  tickIntervalMs?: number;
  /** kind: whirlwind — base per-tick damage (scaled via `scale`). */
  tickDamage?: number;
  /** kind: whirlwind — the caster's move speed is x (1 - selfSlow) while active. */
  selfSlow?: number;
  /**
   * feature 1.3a / 1.4 — timed statuses applied to EVERY enemy the ability
   * damages (or, for a 0-damage effect, every enemy in the area / the target),
   * sourceId = caster. Threaded through World.dealAbilityDamage. Every entry in
   * the array is applied. Honoured by nuke / aoe / meleeArc / projectile.
   */
  onHitStatuses?: Array<{
    kind: StatusKind;
    durationMs: number;
    magnitude: number;
    /** feature 1.3b — periodic interval (ms) for a `dot` applied on hit (Moonfire). */
    tickIntervalMs?: number;
  }>;
  /** feature 1.3a — client FX variant key; overrides a handler's default FX. */
  fxKind?: CombatFx['kind'];
}

export type AbilityBook = Record<AbilitySlot, AbilityDef>;

/**
 * Per-class ability data. Every class ships a full real Q/W/E/R book as of
 * feature 1.4 (the shared 1.1 `_placeholder` book was deleted in the 1.4
 * cleanup).
 */
export type AbilityData = Record<ClassId, AbilityBook>;

/**
 * feature 5.1a / 5.1b — the AI archetype that drives the `tickEnemy` dispatch.
 * `ranged` / `caster` (5.1b) kite to `preferredRange` and attack from afar via a
 * hostile projectile / hostile ground-AoE telegraph respectively.
 */
export type EnemyAi = 'melee' | 'brute' | 'swarm' | 'kamikaze' | 'ranged' | 'caster';

/**
 * feature 5.1a / 5.1b — every enemy catalog key (`DATA.enemies`), in declaration
 * order. 5.1b adds `goblinArcher` / `spiderSpitter` (`ranged`), `goblinShaman`
 * (`caster`) and the two `melee` "elites" `skeletonKnight` / `witchHound`.
 */
export type EnemyType =
  | 'goblin'
  | 'goblinBrute'
  | 'ratSwarm'
  | 'impBomber'
  | 'goblinArcher'
  | 'goblinShaman'
  | 'spiderSpitter'
  | 'skeletonKnight'
  | 'witchHound';

/**
 * feature 5.1b — a status an enemy attack (melee hit, hostile projectile, or
 * hostile ground-AoE) applies to the hero it lands on. Same shape as an
 * ability's `onHitStatuses` entry; the hero's shield / damageReduction still
 * interpose on the *damage*, the status is applied regardless.
 */
export interface EnemyOnHitStatus {
  kind: StatusKind;
  durationMs: number;
  magnitude: number;
  /** periodic interval (ms) for a `dot` applied on hit (witchHound). */
  tickIntervalMs?: number;
}

/** feature 5.1b — `ranged` enemy projectile def (`goblinArcher` / `spiderSpitter`). */
export interface EnemyProjectileDef {
  speed: number;
  damage: number;
  range: number;
  hitRadius: number;
  cooldownMs: number;
}

/** feature 5.1b — `caster` enemy delayed ground-AoE def (`goblinShaman`). */
export interface EnemyGroundAoeDef {
  windupMs: number;
  radius: number;
  damage: number;
  cooldownMs: number;
  /** max distance from the caster to the targeted hero to start the windup. */
  castRange: number;
}

/** feature 5.1b — `caster` enemy heal def: tops up wounded *enemies* nearby. */
export interface EnemyHealDef {
  amount: number;
  radius: number;
  cooldownMs: number;
}

export interface EnemyStats {
  name: string;
  hp: number;
  speed: number;
  damage: number;
  attackRange: number;
  attackSpeed: number;
  radius: number;
  armor: number;
  xpReward: number;
  goldReward: number;
  /** feature 5.1a — AI archetype (`sim/world` `tickEnemy` dispatch). */
  ai: EnemyAi;
  /** feature 5.1a — `brute`: px the target hero is shoved away on a landed
   * melee hit (arena-collided). Absent / 0 for other archetypes. */
  knockback?: number;
  /** feature 5.1a — `kamikaze`: explode when a living hero is within this
   * distance (in addition to exploding on death). */
  contactRadius?: number;
  /** feature 5.1a — `kamikaze`: explosion damage to every hero in range
   * (armor + shield + damageReduction mitigated, like goblin melee). */
  explosionDamage?: number;
  /** feature 5.1a — `kamikaze`: explosion radius, in px. */
  explosionRadius?: number;
  /** feature 5.1b — `ranged` / `caster`: kite so the nearest hero sits ~this far
   * away (approach past it, retreat inside `preferredRange * 0.7`). */
  preferredRange?: number;
  /** feature 5.1b — a status applied to the hero on any landed melee hit /
   * hostile projectile hit / hostile ground-AoE (`skeletonKnight` stun,
   * `witchHound` dot, `spiderSpitter` slow). */
  onHitStatus?: EnemyOnHitStatus;
  /** feature 5.1b — `ranged`: the hostile projectile fired at the target. */
  projectile?: EnemyProjectileDef;
  /** feature 5.1b — `caster`: the hostile delayed ground-AoE. */
  groundAoe?: EnemyGroundAoeDef;
  /** feature 5.1b — `caster`: the wounded-ally heal pulse. */
  heal?: EnemyHealDef;
}

/** feature 5.1a — `DATA.waves.waveTable`; the deterministic wave-composition
 * config consumed by `waveComposition` (`sim/waves.ts`). All numbers are
 * untuned placeholders. */
export interface WaveTable {
  budgetPerWave: number;
  goblinOnlyThroughWave: number;
  bruteFromWave: number;
  bruteBudget: number;
  bruteChance: number;
  ratFromWave: number;
  ratPackBudget: number;
  ratPackMin: number;
  ratPackMax: number;
  ratPacksMax: number;
  ratChance: number;
  impFromWave: number;
  impBudget: number;
  impMax: number;
  impChance: number;
  /** feature 5.1b — `goblinArcher` slot: gate wave / budget cost / per-slot roll. */
  archerFromWave: number;
  archerBudget: number;
  archerChance: number;
  /** feature 5.1b — `goblinShaman` slot (capped at `shamanMax` per wave). */
  shamanFromWave: number;
  shamanBudget: number;
  shamanChance: number;
  shamanMax: number;
  /** feature 5.1b — `spiderSpitter` slot (occasional). */
  spitterFromWave: number;
  spitterBudget: number;
  spitterChance: number;
  /** feature 5.1b — the two "elite" types (`skeletonKnight` / `witchHound`);
   * a matched slot draws a second rng roll (50/50) to pick which one. */
  eliteFromWave: number;
  eliteBudget: number;
  eliteChance: number;
}

/**
 * feature 5.2 — one boss ability. A superset of the 5.1b enemy attack defs.
 * `kind` picks the resolver in `World.resolveBossAbility`:
 * - `groundAoe` — a telegraphed circle at the boss's position; on resolve,
 *   `damage` + optional radial `knockback` to heroes inside `radius`. `windupMs`
 *   is the danger-zone telegraph time that follows the `castMs` cast bar.
 * - `projectileVolley` — `projectiles` hostile shots in a `spreadDeg` fan toward
 *   the nearest hero (`projectileSpeed` / `projectileRange` / `hitRadius`).
 * - `summon` — spawn each `spawn` entry's `count` of `etype` near the boss
 *   (add-spawn jitter is the only place the boss script touches `rng`).
 * - `dashLine` — telegraph a circle at the farthest hero's position (clamped to
 *   `maxRange`), then dash there in a straight line; heroes within
 *   `corridorWidth / 2` of the line take `damage` + `knockback`. The telegraph
 *   is a circle at the destination, not a true line — see deferred-questions 5.2.
 * - `melee` — instant; `damage` (+ `onHitStatus`) to every hero within `radius`
 *   of the boss body. The rotation fallback between cooldowns.
 * - `hazardSpawn` (feature 5.3a) — telegraph `count` circles (`radius`,
 *   `telegraphMs`) at hero positions; on resolve each becomes a floor hazard
 *   (`hazardKind` / `hazardMagnitude` / `hazardDurationMs`). The Bramble Matron's
 *   `vineEruption`.
 * - `selfShield` (feature 5.3a) — the boss ignores all damage for `durationMs`
 *   (reuses `bossDamageBlocked`); it does NOT reset the rotation, count as a
 *   phase transition, or freeze the boss (it keeps moving / casting). The Frost
 *   Revenant's `hoarfrostGuard`.
 * All numbers are placeholders.
 */
export type BossAbilityKind =
  | 'groundAoe'
  | 'projectileVolley'
  | 'summon'
  | 'dashLine'
  | 'melee'
  | 'hazardSpawn'
  | 'selfShield'
  /** feature 5.3c — arena-wide unavoidable damage on resolve; the cast bar IS the
   * warning (no telegraph). `damage` to every living hero NOT inside a hazard of
   * `exemptHazardKind` (Furnace Golem `overheatPulse`, Arena Master `furnacePulse`). */
  | 'arenaDamage';

/**
 * feature 5.3a / 5.3c — a floor-hazard zone kind.
 * - `damage` — hits a hero whose centre is INSIDE `radius`, once per `HAZARD_TICK_MS`.
 * - `slow` — refreshes a short slow every tick while a hero is inside `radius`.
 * - `burnOutside` (5.3c) — the inverse of `damage`: hits a hero whose centre is
 *   OUTSIDE `radius`, once per `HAZARD_TICK_MS`, UNLESS the hero stands in a
 *   `safe` hazard (the Furnace Golem's shrinking safe zone).
 * - `safe` (5.3c) — no effect on its own; suppresses `burnOutside` (and `damage`)
 *   ticks and `arenaDamage` for a hero standing in it (the "cooling vents").
 */
export type HazardKind = 'damage' | 'slow' | 'burnOutside' | 'safe';

export interface BossAbilityDef {
  id: string;
  kind: BossAbilityKind;
  /** cast-bar time before the ability resolves (ms). Omitted / 0 → instant. */
  castMs?: number;
  /** re-use cooldown, started when the ability resolves (ms). */
  cooldownMs: number;
  /** groundAoe / melee — effect radius. */
  radius?: number;
  /** groundAoe / dashLine / melee / projectileVolley — hit damage. */
  damage?: number;
  /** groundAoe — danger-zone telegraph windup after the cast bar (ms). */
  windupMs?: number;
  /** groundAoe (radial) / dashLine (along the line) — hero knockback distance (px). */
  knockback?: number;
  /** dashLine — max dash distance (px); the destination is clamped to it. */
  maxRange?: number;
  /** dashLine — full corridor width for the en-route hero scan (px). */
  corridorWidth?: number;
  /** dashLine — destination-telegraph windup before the dash executes (ms). */
  telegraphMs?: number;
  /** projectileVolley — number of shots. */
  projectiles?: number;
  /** projectileVolley — total fan spread (degrees). */
  spreadDeg?: number;
  /** projectileVolley — shot speed (px/s). */
  projectileSpeed?: number;
  /** projectileVolley — shot max travel (px). */
  projectileRange?: number;
  /** projectileVolley — shot hit radius (px). */
  hitRadius?: number;
  /** summon — the adds to spawn near the boss. */
  spawn?: { etype: EnemyType; count: number }[];
  /** melee / dashLine / groundAoe — status applied to every hero the hit lands on. */
  onHitStatus?: EnemyOnHitStatus;
  /** feature 5.3a — hazardSpawn: number of hazard circles to telegraph. */
  count?: number;
  /** feature 5.3a — hazardSpawn: the kind each telegraphed circle becomes. */
  hazardKind?: HazardKind;
  /** feature 5.3a — hazardSpawn: `magnitude` of the spawned hazard (damage per
   * `HAZARD_TICK_MS` for `damage`, slow fraction for `slow`). */
  hazardMagnitude?: number;
  /** feature 5.3a — hazardSpawn: lifetime (ms) of each spawned hazard. */
  hazardDurationMs?: number;
  /** feature 5.3a — selfShield: invuln duration (ms). */
  durationMs?: number;
  /** feature 5.3a — groundAoe: fire this many staggered circles instead of one,
   * each `staggerRadiusStep` px larger and `staggerDelayMs` later (Frost
   * Revenant `frostNova`). Omitted / 1 → the plain single-circle 5.2 behaviour. */
  stagger?: number;
  staggerRadiusStep?: number;
  staggerDelayMs?: number;
  /** feature 5.3b — `dashLine` variant: run the corridor sweep + damage but do
   * NOT teleport the boss (a projectile-like tornado, not a charge). The Gale
   * Twins' `tornadoLine`. */
  noMove?: boolean;
  /** feature 5.3b — `groundAoe` variant: knock every hit hero directly AWAY from
   * the arena centre (toward the lethal edge) instead of away from the blast
   * centre. The Gale Twins' `gust`. */
  knockbackFromArenaCentre?: boolean;
  /** feature 5.3c — `arenaDamage`: heroes standing in a hazard of this kind are
   * exempt from the hit (`'safe'` — stand on a cooling vent). */
  exemptHazardKind?: HazardKind;
  /** feature 5.3c — `dashLine` variant: on resolve, drop a line of lingering
   * floor-hazard circles along the dash path (Furnace Golem `lavaSweep`). */
  lingerHazardKind?: HazardKind;
  lingerHazardMagnitude?: number;
  lingerHazardDurationMs?: number;
  lingerHazardRadius?: number;
}

/**
 * feature 5.2 — one boss phase. When the boss's hp fraction drops to/below
 * `hpFrac`, this phase's ability `rotation` (ability ids, cycled) becomes active
 * and `castGapMs` (the pacing pause between abilities) applies. Phases are
 * ordered high→low `hpFrac`; `1.0` is the opening phase.
 */
export interface PhaseDef {
  hpFrac: number;
  rotation: string[];
  /** ms the boss waits between finishing one ability and starting the next. */
  castGapMs: number;
  /** feature 5.3c — adds spawned near the boss ON ENTERING this phase ("adds
   * every 20%" — the Arena Master). Also spawned for the opening phase on
   * `spawnBoss`. */
  summonAdds?: { etype: EnemyType; count: number }[];
  /** feature 5.3c — while the boss is in this phase, its outgoing ability / melee
   * damage is ×`damageMul`, its move speed ×`speedMul`, and its `castGapMs`
   * ×`castGapMul` (each defaults to 1). The Arena Master's final-phase enrage. */
  enrage?: { damageMul?: number; speedMul?: number; castGapMul?: number };
}

/**
 * feature 5.2 — a data-driven boss (`DATA.bosses`, keyed by id). A boss is a
 * `SimEnemy` with `boss: true` (so existing death / targeting / separation code
 * just works) plus the boss runtime fields on `SimEnemy`. `onDeath`: `clearWave`
 * (wave 5 — the boss dying clears the wave) or `winRun` (wave 30, feature 5.4).
 */
export interface BossDef {
  name: string;
  hp: number;
  armor: number;
  radius: number;
  moveSpeed: number;
  spawnWave: number;
  onDeath: 'clearWave' | 'winRun';
  xpReward: number;
  goldReward: number;
  /** invulnerability window (ms) applied on every phase transition. */
  invulnMs: number;
  phases: PhaseDef[];
  abilities: Record<string, BossAbilityDef>;
  /** extra obstacle rects added to the arena while this boss is alive (§D14). */
  pillars?: { id: string; x: number; y: number; w: number; h: number }[];
  /** feature 5.3a — one large floor hazard spawned at the arena centre on boss
   * spawn and lasting the whole fight (the Frost Revenant's "icy floor"). */
  floorHazard?: { radius: number; kind: HazardKind; magnitude: number; durationMs: number };
  /**
   * feature 5.3c — bespoke arena hazards spawned on boss spawn, each at
   * `(arenaCentre + offsetX/offsetY)` and lasting the whole fight. Used for the
   * Furnace Golem's shrinking `burnOutside` safe zone (`shrinkPerMs` closes it to
   * `minRadius`) plus the fixed `safe` cooling vents.
   */
  arenaHazards?: {
    offsetX?: number;
    offsetY?: number;
    radius: number;
    kind: HazardKind;
    magnitude: number;
    durationMs: number;
    shrinkPerMs?: number;
    minRadius?: number;
  }[];
  /**
   * feature 5.3b — multi-boss support. `spawnBoss` spawns `spawnCount` (default
   * 1) boss `SimEnemy` entities sharing this def, each at full `hp`, spread
   * around the 5.2 spawn point. A `spawnCount` of 1 / absent is byte-identical to
   * 5.2 (one entity, same position). `linkMode: "revive"` links the group: a
   * twin brought to 0 hp goes `downed` (stops acting) for `reviveWindowMs`; if a
   * linked partner is still up when the timer expires it revives at
   * `maxHp * reviveHpFrac`; if every linked twin is downed at once they all die
   * together (→ `onDeath`). The only `linkMode` for now.
   */
  spawnCount?: number;
  linkMode?: 'revive';
  reviveWindowMs?: number;
  reviveHpFrac?: number;
  /**
   * feature 5.3b — "the edges are lethal": on spawn, a ring of overlapping large
   * `damage` hazard circles is placed `inset` px inside the arena walls, walked
   * clockwise around the perimeter in `count` steps, each of `radius` and
   * `magnitude` (damage per `HAZARD_TICK_MS`), lasting `durationMs`.
   */
  edgeHazard?: {
    count: number;
    radius: number;
    magnitude: number;
    inset: number;
    durationMs: number;
  };
}

/** feature 5.5 — the host-picked difficulty tier; a flat multiplier on every
 * enemy/boss hp/damage/armor (see `EntitiesData.difficulty`, `World.setDifficulty`). */
export type DifficultyTier = 'easy' | 'normal' | 'medium' | 'hard' | 'brutal';
export const DIFFICULTY_TIERS: DifficultyTier[] = ['easy', 'normal', 'medium', 'hard', 'brutal'];

export interface EntitiesData {
  players: { moveSpeed: number; hitboxRadius: number; reviveHpFactor: number };
  /** feature 5.5 — the flat difficulty-tier multiplier catalog (see `DifficultyTier`). */
  difficulty: Record<DifficultyTier, number>;
  /** feature 5.6 — the run-long, team-shared wave-end revival token budget per
   * tier; `null` = unlimited (easy/normal/medium, today's pre-5.6 behaviour).
   * `World.revivalTokens` is seeded from this on `beginMatch` (see
   * `World.enterWaveBreak`'s gating of the dead-player revive branch). */
  revivalTokens: Record<DifficultyTier, number | null>;
  /** feature 2.1 — gold / shared-XP economy tunables. */
  economy: {
    goldLossOnDeathFrac: number;
    waveClearGoldBase: number;
    waveClearGoldPerWave: number;
  };
  attributes: AttributeFormula;
  /** feature 4.1 — leveling curve + level-up rewards (see `Progression`). */
  progression: Progression;
  classes: Record<ClassId, ClassStats>;
  abilities: AbilityData;
  /** feature 4.3 — one passive-modifier talent tree per ability, keyed by
   * ability id (see `TalentNode`, `sim/talents.ts`). */
  talents: Record<string, TalentNode[]>;
  /** feature 5.1a — the keyed enemy catalog (see `EnemyStats`, `EnemyType`). */
  enemies: Record<EnemyType, EnemyStats>;
  /** feature 5.2 — the keyed boss catalog (see `BossDef`). */
  bosses: Record<string, BossDef>;
  waves: {
    waveBreakSeconds: number;
    selectCountdownSeconds: number;
    /** feature 5.1a — deterministic wave-composition config. */
    waveTable: WaveTable;
  };
  /** feature 2.3 — the ordered, fixed shop catalog (see `ShopItem`). */
  shop: ShopItem[];
  /** feature 3.1 — the fixed item catalog, keyed by id (see `ItemDef`). */
  items: Record<string, ItemDef>;
  /** feature 3.3 — the ordered forge recipe list (see `RecipeDef`). */
  recipes: RecipeDef[];
  /** feature 3.4 — the trash-mob enemy item drop table (see `DropTable`). */
  drops: DropTable;
}

/** feature 3.1 — every item catalog id, in `entities.json` declaration order. */
export const ITEM_IDS = Object.keys(entities.items);

/** feature 3.3 — every forge recipe id, in `entities.json` declaration order. */
export const RECIPE_IDS = entities.recipes.map((r) => r.id);

/** feature 5.1a — every enemy catalog key, in `entities.json` declaration order. */
export const ENEMY_TYPE_IDS = Object.keys(entities.enemies) as EnemyType[];

/** feature 5.2 — every boss catalog key, in `entities.json` declaration order.
 * The `_comment` doc key is filtered out (as `talents.ts` does for its book). */
export const BOSS_IDS = Object.keys(entities.bosses).filter((k) => k !== '_comment');

export const DATA = entities as unknown as EntitiesData;

/**
 * Resolve a class's ability kit. Every class has a full real book as of feature
 * 1.4. A null / unknown class (spectator, not-yet-selected) falls back to the
 * Warrior book as a defensive default so callers never get `undefined`.
 */
export function abilityBook(cls: ClassId | null | undefined): AbilityBook {
  return (cls && DATA.abilities[cls]) || DATA.abilities.warrior;
}

/** Player input snapshot sent from client to server. */
export interface PlayerInput {
  /** Monotonic sequence number, used for client-side reconciliation. */
  seq: number;
  /** Normalised-ish keyboard direction, each component in [-1, 1]. */
  moveX: number;
  moveY: number;
  /** Pending click-to-move destination in world coords, or null. */
  target: { x: number; y: number } | null;
  /** True on the frame the player issued a fresh click (to (re)plan a path). */
  clickTick: boolean;
}

export const EMPTY_INPUT: PlayerInput = {
  seq: 0,
  moveX: 0,
  moveY: 0,
  target: null,
  clickTick: false,
};

/** Transient combat visual, broadcast outside the synced state. */
export interface CombatFx {
  kind:
    | 'melee'
    | 'ranged'
    | 'enemyMelee'
    | 'hit'
    | 'abilityBolt'
    | 'abilityBlink'
    | 'abilityHeal'
    | 'abilityNova'
    // 1.2 Warrior kit
    | 'abilityCharge'
    | 'abilityCleave'
    | 'abilityBulwark'
    | 'abilityWhirlwind'
    // 1.3a Wizard / Ranger kits
    | 'abilityFireball'
    | 'abilityFrostNova'
    | 'abilityBarrier'
    // 1.3b Cleric / Druid kits
    | 'abilitySmite'
    | 'abilityCircleHeal'
    | 'abilityBlessing'
    | 'abilityMoonfire'
    | 'abilityRegrowth'
    | 'abilityRoots'
    // 1.3c Warlock kit
    | 'abilityShadowBolt'
    | 'abilityCorruption'
    | 'abilityDrainLife'
    // 1.4 ultimates
    | 'abilityMeteor'
    | 'abilityRapidFire'
    | 'abilitySanctuary'
    | 'abilityEntanglingStorm'
    | 'abilitySoulHarvest'
    // 1.5 — Piercing Shot muzzle flash (the projectile itself is synced state)
    | 'abilityPierce'
    // 5.1a — kamikaze (impBomber) detonation blast; also the 5.1b hostile
    // ground-AoE (goblinShaman) resolution blast
    | 'enemyExplode'
    // 5.1b — a hostile projectile muzzle flash (archer / spitter)
    | 'enemyShoot'
    // 5.1b — a goblinShaman heal pulse on nearby wounded enemies
    | 'enemyHeal';
  x: number;
  y: number;
  tx?: number;
  ty?: number;
  /** kind: abilityNova / abilityWhirlwind / abilityFrostNova / abilityCleave /
   * abilityMeteor / abilitySanctuary / abilityEntanglingStorm / abilitySoulHarvest
   * / enemyExplode — effect radius, for the client ring / cone. */
  radius?: number;
  tint?: string;
}

export interface Vec2 {
  x: number;
  y: number;
}
